import React from 'react';
import type { Monitor } from '../../../types/Monitor';
import type { UserMonitorConfig } from '../../../types/UserMonitorConfig';
import { useScaleFactor } from '../../../hooks/useScaleFactor';
import { Wallpaper } from '../Wallpaper';
import { MenuBar } from '../MenuBar';
import { Dock } from '../Dock';
import { FinderWindow } from '../Window';
import styles from './Desktop.module.css';

interface DesktopProps {
  monitor: Monitor;
  userConfig: UserMonitorConfig;
  /** The logical resolution of the chosen scaling preset for the target monitor. */
  scalingPreset: { logicalW: number; logicalH: number };
}

export function Desktop({ monitor, userConfig, scalingPreset }: DesktopProps) {
  const { previewScale, logicalResolution, targetPpi, userPpi } =
    useScaleFactor(userConfig, monitor, scalingPreset);

  const { logicalW, logicalH } = logicalResolution;

  return (
    <div className={styles.outer}>
      {/* The scaled desktop viewport */}
      <div
        className={styles.desktopContent}
        style={{
          width: `${logicalW}px`,
          height: `${logicalH}px`,
          transform: `scale(${previewScale})`,
          transformOrigin: 'top left',
        }}
      >
        <Wallpaper />
        <MenuBar />
        <FinderWindow
          containerW={logicalW}
          containerH={logicalH}
          previewScale={previewScale}
        />
        <Dock />

        {/* Diagonal ruler annotation */}
        <DiagonalRuler
          logicalW={logicalW}
          logicalH={logicalH}
          diagonalInches={monitor.diagonalInches}
        />
      </div>

      {/* Info strip below the canvas */}
      <div className={styles.infoStrip}>
        <InfoPill label="Logical res" value={`${logicalW} × ${logicalH}`} />
        <InfoPill label="Native" value={`${monitor.nativeResolutionW} × ${monitor.nativeResolutionH}`} />
        <InfoPill label="Target PPI" value={targetPpi.toFixed(0)} />
        <InfoPill label="Your PPI" value={userPpi.toFixed(0)} />
        <InfoPill label="Preview scale" value={`${previewScale.toFixed(3)}×`} highlight />
      </div>
    </div>
  );
}

/* ─── Diagonal ruler ─────────────────────────────────────────── */

interface DiagonalRulerProps {
  logicalW: number;
  logicalH: number;
  diagonalInches: number;
}

function DiagonalRuler({ logicalW, logicalH, diagonalInches }: DiagonalRulerProps) {
  // Draw a dashed line from top-left to bottom-right with a label
  const cx = logicalW / 2;
  const cy = logicalH / 2;
  const halfW = logicalW * 0.3;
  const halfH = logicalH * 0.3;

  const x1 = cx - halfW;
  const y1 = cy - halfH;
  const x2 = cx + halfW;
  const y2 = cy + halfH;

  // Label midpoint
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;

  const angle = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;

  return (
    <svg
      className={styles.ruler}
      style={{ width: logicalW, height: logicalH }}
      viewBox={`0 0 ${logicalW} ${logicalH}`}
      aria-label={`${diagonalInches}" diagonal ruler`}
    >
      {/* Dashed diagonal line */}
      <line
        x1={x1} y1={y1} x2={x2} y2={y2}
        stroke="rgba(255,255,255,0.35)"
        strokeWidth="1.5"
        strokeDasharray="6 4"
      />
      {/* End caps */}
      <line x1={x1 - 6} y1={y1} x2={x1 + 6} y2={y1} stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" transform={`rotate(${angle - 90}, ${x1}, ${y1})`} />
      <line x1={x2 - 6} y1={y2} x2={x2 + 6} y2={y2} stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" transform={`rotate(${angle - 90}, ${x2}, ${y2})`} />

      {/* Label background pill */}
      <rect
        x={mx - 36} y={my - 11}
        width="72" height="22"
        rx="11"
        fill="rgba(0,0,0,0.42)"
        style={{ backdropFilter: 'blur(6px)' }}
      />
      <text
        x={mx} y={my + 4.5}
        textAnchor="middle"
        fontSize="12"
        fontWeight="600"
        fontFamily="-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif"
        fill="white"
        fillOpacity="0.9"
        letterSpacing="-0.2"
      >
        {diagonalInches}"
      </text>
    </svg>
  );
}

/* ─── Info pill ──────────────────────────────────────────────── */

interface InfoPillProps {
  label: string;
  value: string;
  highlight?: boolean;
}

function InfoPill({ label, value, highlight = false }: InfoPillProps) {
  return (
    <div className={`${styles.pill} ${highlight ? styles.pillHighlight : ''}`}>
      <span className={styles.pillLabel}>{label}</span>
      <span className={styles.pillValue}>{value}</span>
    </div>
  );
}
